import React, { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { IMAGES } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";

const CATEGORIES = [
  { value: "logo", label: "Logo" },
  { value: "social", label: "Redes sociales" },
  { value: "banner", label: "Banner" },
  { value: "campaign", label: "Campaña" },
  { value: "certification", label: "Certificación" },
  { value: "seal", label: "Sello" },
  { value: "other", label: "Otro" },
];

export default function Images() {
  const [images, setImages] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "logo", url: "", alt_text: "" });

  const load = useCallback(async () => {
    const { data } = await api.get("/images");
    setImages(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.url) {
      toast.error("Nombre y URL son requeridos");
      return;
    }
    try {
      await api.post("/images", form);
      toast.success("Imagen añadida");
      setOpen(false);
      setForm({ name: "", category: "logo", url: "", alt_text: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/images/${id}`);
      toast.success("Imagen eliminada");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6" data-testid={IMAGES.list}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-600 mb-1">
            Recursos gráficos
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Imágenes</h1>
          <p className="text-sm text-slate-500 mt-1">
            Logos, banners, sellos y certificaciones disponibles para las firmas.
          </p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          className="bg-orange-600 hover:bg-orange-700 text-white transition-colors"
          data-testid={IMAGES.addBtn}
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.75} />
          Añadir imagen
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((img) => (
          <Card key={img.id} className="border-slate-200 shadow-sm overflow-hidden">
            <div className="bg-slate-100 p-6 flex items-center justify-center min-h-[140px] border-b border-slate-200">
              <img src={img.url} alt={img.alt_text} className="max-h-24 object-contain" />
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{img.name}</div>
                  <Badge className="mt-1 bg-slate-100 text-slate-700 hover:bg-slate-100 border-slate-200 text-[10px]">
                    {CATEGORIES.find((c) => c.value === img.category)?.label || img.category}
                  </Badge>
                </div>
                <Button
                  size="sm" variant="outline"
                  onClick={() => remove(img.id)}
                  className="border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200"
                  data-testid={IMAGES.deleteBtn(img.id)}
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                </Button>
              </div>
              <div className="text-[11px] text-slate-400 font-mono mt-2 truncate">{img.url}</div>
            </div>
          </Card>
        ))}
        {images.length === 0 && (
          <div className="col-span-full text-center py-12 border border-dashed border-slate-200 rounded-md bg-white text-sm text-slate-400">
            <ImageIcon className="w-8 h-8 mx-auto mb-2" strokeWidth={1.5} />
            No hay imágenes. Añade la primera para empezar.
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Nueva imagen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid={IMAGES.nameInput} className="mt-1 focus-visible:ring-orange-500" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Categoría</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="mt-1 focus:ring-orange-500" data-testid={IMAGES.categorySelect}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">URL</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://..." data-testid={IMAGES.urlInput}
                className="mt-1 focus-visible:ring-orange-500 font-mono text-xs" />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Texto alternativo</Label>
              <Input value={form.alt_text} onChange={(e) => setForm({ ...form, alt_text: e.target.value })}
                className="mt-1 focus-visible:ring-orange-500" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-200">Cancelar</Button>
            <Button onClick={create} className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid={IMAGES.saveBtn}>
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
